import { useCallback } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { MergeIntegration } from 'libs/end-to-end/src/types/databases/integrations/merge-integration';
import { MergeIntegrationPassthroughRequest } from 'libs/end-to-end/src/types/databases/integrations/merge-integration';
import { QuestionRubric } from 'libs/end-to-end/src/types/ai-interviewer/ai-interviewer-rubric';
import { ResumeQualityScoreRubric } from 'libs/end-to-end/src/types/resume-parser/resume-quality-score-rubric';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { getMergeCategoryEnumForString } from 'libs/end-to-end/src/merge';
import { AI_INTERVIEWER_DEBUG_MODE_DEFAULT } from 'libs/end-to-end/src/types/ai-interviewer/ai-interviewer-debug-mode';
import { AIInterviewerDebugMode } from 'libs/end-to-end/src/types/ai-interviewer/ai-interviewer-debug-mode';
import { ResumeParsingCacheBusterDefaultVersion } from 'libs/end-to-end/src/types/databases/integrations/resume-parsing-cache-buster';
import { AllowedOpenAiModels } from 'libs/end-to-end/src/types/databases/data-sets/data-set-resume-rubric';

export interface DataSetEditMergeIntegrationHook {
  mergeIntegration: MergeIntegration;
  setMergeIntegration: Setter<MergeIntegration>;
}

export const useDataSetEditMergeIntegration =
  (): DataSetEditMergeIntegrationHook => {
    const [linkToken, setLinkToken] = useState<string>('');
    const [magicLinkUrl, setMagicLinkUrl] = useState<string>('');
    const [accountToken, setAccountToken] = useState<string>('');
    const [category, setCategory] = useState<string>('');
    const [aiInterviewerEnabled, setAiInterviewerEnabled] =
      useState<boolean>(false);
    const [aiInterviewerDebugMode, setAiInterviewerDebugMode] =
      useState<AIInterviewerDebugMode>(AI_INTERVIEWER_DEBUG_MODE_DEFAULT);
    const [aiInterviewerStartDate, setAiInterviewerStartDate] =
      useState<string>('');
    const [questionRubrics, setQuestionRubrics] = useState<QuestionRubric[]>(
      [],
    );

    const [isResumeParsingEnabled, setIsResumeParsingEnabled] =
      useState<boolean>(false);
    const [resumeQualityScoreEnabled, setResumeQualityScoreEnabled] =
      useState<boolean>(false);

    const [isIncrementalSyncEnabled, setIsIncrementalSyncEnabled] =
      useState<boolean>(false);

    const [
      isResumeParsingFilteringDisabled,
      setIsResumeParsingFilteringDisabled,
    ] = useState<boolean>(false);

    const [isConvertToParquetEnabled, setIsConvertToParquetEnabled] =
      useState<boolean>(false);

    const [isSnowflakeIntegrationEnabled, setIsSnowflakeIntegrationEnabled] =
      useState<boolean>(false);

    const [isResumeExtraFeaturesEnabled, setIsResumeExtraFeaturesEnabled] =
      useState<boolean>(false);

    const [resumeQualityScoreRubrics, setResumeQualityScoreRubrics] = useState<
      ResumeQualityScoreRubric[]
    >([]);
    const [resumeQualityScoreUpdatedAt, setResumeQualityScoreUpdatedAt] =
      useState<number>(0);
    const [passthroughRequests, setPassthroughRequests] = useState<
      MergeIntegrationPassthroughRequest[]
    >([]);

    const [openAiModel, setOpenAiModel] = useState<AllowedOpenAiModels>(
      AllowedOpenAiModels.parse('gpt-4o-mini'),
    );

    const [
      resumeParsingCacheBusterVersion,
      setResumeParsingCacheBusterVersion,
    ] = useState<string>(ResumeParsingCacheBusterDefaultVersion);

    const setMergeIntegration = useCallback(
      (mergeIntegration: MergeIntegration) => {
        setLinkToken(mergeIntegration.linkToken);
        setMagicLinkUrl(mergeIntegration.magicLinkUrl);
        setAccountToken(mergeIntegration.accountToken);
        setCategory(mergeIntegration.category);
        setAiInterviewerEnabled(mergeIntegration.aiInterviewerEnabled ?? false);
        setAiInterviewerDebugMode(
          mergeIntegration.aiInterviewerDebugMode ??
            AI_INTERVIEWER_DEBUG_MODE_DEFAULT,
        );
        setAiInterviewerStartDate(
          mergeIntegration.aiInterviewerStartDate ?? '',
        );
        setQuestionRubrics(mergeIntegration.questionRubrics || []);

        setResumeQualityScoreRubrics(
          mergeIntegration.resumeQualityScoreRubrics || [],
        );
        setIsResumeParsingEnabled(
          mergeIntegration.isResumeParsingEnabled ?? false,
        );
        setIsConvertToParquetEnabled(
          mergeIntegration.isConvertToParquetEnabled ?? false,
        );
        setResumeQualityScoreEnabled(
          mergeIntegration.resumeQualityScoreEnabled ?? false,
        );

        setIsSnowflakeIntegrationEnabled(
          mergeIntegration.snowflakeIntegrationEnabled ?? false,
        );

        setResumeQualityScoreUpdatedAt(Date.now());

        setPassthroughRequests(mergeIntegration.passthroughRequests || []);

        setIsIncrementalSyncEnabled(mergeIntegration.isIncrementalSyncEnabled);

        setIsResumeParsingFilteringDisabled(
          mergeIntegration.isResumeParsingFilteringDisabled,
        );

        setResumeParsingCacheBusterVersion(
          mergeIntegration.resumeParsingCacheBusterVersion,
        );

        setIsResumeExtraFeaturesEnabled(
          mergeIntegration.isResumeExtraFeaturesEnabled,
        );

        setOpenAiModel(mergeIntegration.openAiModel);
      },
      [],
    );

    return useMemo(() => {
      return {
        mergeIntegration: {
          integrationType: 'Merge',
          linkToken,
          magicLinkUrl,
          accountToken,
          category: getMergeCategoryEnumForString(category),
          aiInterviewerEnabled,
          aiInterviewerDebugMode,
          aiInterviewerStartDate,
          questionRubrics,
          isResumeParsingEnabled: isResumeParsingEnabled,
          isConvertToParquetEnabled: isConvertToParquetEnabled,
          resumeQualityScoreEnabled: resumeQualityScoreEnabled,
          resumeQualityScoreRubrics: resumeQualityScoreRubrics,
          resumeQualityScoreUpdatedAt: resumeQualityScoreUpdatedAt,
          passthroughRequests: passthroughRequests,
          isIncrementalSyncEnabled: isIncrementalSyncEnabled,
          isResumeParsingFilteringDisabled: isResumeParsingFilteringDisabled,
          isResumeExtraFeaturesEnabled: isResumeExtraFeaturesEnabled,
          resumeParsingCacheBusterVersion: resumeParsingCacheBusterVersion,
          snowflakeIntegrationEnabled: isSnowflakeIntegrationEnabled,
          openAiModel: openAiModel,
        },
        setMergeIntegration,
      };
    }, [
      linkToken,
      magicLinkUrl,
      accountToken,
      category,
      aiInterviewerEnabled,
      aiInterviewerDebugMode,
      aiInterviewerStartDate,
      questionRubrics,
      isResumeParsingEnabled,
      isConvertToParquetEnabled,
      isSnowflakeIntegrationEnabled,
      resumeQualityScoreEnabled,
      resumeQualityScoreRubrics,
      resumeQualityScoreUpdatedAt,
      passthroughRequests,
      setMergeIntegration,
      isIncrementalSyncEnabled,
      isResumeParsingFilteringDisabled,
      isResumeExtraFeaturesEnabled,
      resumeParsingCacheBusterVersion,
      openAiModel,
    ]);
  };
